import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiLockLine, RiSave2Line } from '@remixicon/react';
import type { ConnectorDto } from '@atnt/shared';
import { ApiError } from '@/shared/api/client';
import { Button, Card, toast } from '@/shared/ui';
import { useHotkey } from '@/features/settings/hotkeys';
import type { ConnectorsContext } from '../backend';
import { useSaveConnector } from '../api';
import type { ConnectorRights } from '../permissions';
import { buildInput, ConnectionTest, ConnectorFields, draftOf, type ConnectorDraft } from './ConnectorForm';
import { SiteCard } from './unified';

/**
 * "Sozlamalar": the connector form inline (read-only without the right; organization connectors need an organization
 * admin). The ATNT unified server also gets the REAL site-code card (`tenants.externalSiteId`).
 */
export function SettingsTab({ c, ctx, rights }: { c: ConnectorDto; ctx: ConnectorsContext; rights: ConnectorRights }) {
  const { t } = useTranslation('connectors');
  const formId = useId();
  const save = useSaveConnector(ctx);
  const editable = rights.canEdit(c);
  const [initial, setInitial] = useState<ConnectorDraft>(() => draftOf(c));
  const [draft, setDraft] = useState(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const unified = c.type === 'atnt_unified';

  const submit = async () => {
    if (!editable || !dirty) return;
    const built = buildInput(draft, 'edit', t);
    if (built.errors) {
      setErrors(built.errors);
      return;
    }
    setErrors({});
    try {
      const saved = await save.mutateAsync({ id: c.id, input: built.input });
      const next = draftOf(saved);
      setInitial(next);
      setDraft(next);
      toast.success(t('panel.saved'), { subtitle: saved.name });
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fieldErrors).length) {
        setErrors(Object.fromEntries(Object.entries(err.fieldErrors).map(([k, v]) => [k.replace(/^config\./, ''), v])));
      } else toast.error(t('error'), { subtitle: err instanceof Error ? err.message : undefined });
    }
  };

  useHotkey('save', () => void submit(), { enabled: editable && dirty });

  return (
    <div className={unified ? 'grid shrink-0 grid-cols-[minmax(0,52rem)_minmax(0,1fr)] items-start gap-10' : 'grid shrink-0 grid-cols-[minmax(0,52rem)] gap-10'}>
      <Card title={t('settings.title')} description={t('settings.desc')}>
        {!editable && (
          <p className="flex items-center gap-8 rounded-8 bg-field px-12 py-8 text-xs text-fg-secondary">
            <RiLockLine aria-hidden className="size-16 shrink-0 text-icon" />
            {c.scope === 'organization' ? t('settings.orgOnly') : t('settings.readOnly')}
          </p>
        )}
        <form
          id={formId}
          noValidate
          className="flex flex-col gap-14"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <ConnectorFields
            draft={draft}
            onChange={(patch) => {
              setDraft((d) => ({ ...d, ...patch }));
              const keys = Object.keys(patch);
              setErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => !keys.some((p) => k === p || k.startsWith(`${p}.`)))));
            }}
            errors={errors}
            mode="edit"
            canChooseOrg={rights.orgAdmin}
            readOnly={!editable}
          />
          {rights.operate && (
            <ConnectionTest
              ctx={ctx}
              connectorId={c.id}
              getConfig={() => {
                const built = buildInput(draft, 'edit', t, true);
                if (built.errors) {
                  setErrors(built.errors);
                  return null;
                }
                return built.config;
              }}
            />
          )}
          {editable && (
            <div className="flex justify-end gap-8">
              <Button
                variant="secondary"
                disabled={!dirty || save.isPending}
                onClick={() => {
                  setDraft(initial);
                  setErrors({});
                }}
              >
                {t('cancel')}
              </Button>
              <Button variant="primary-alt" type="submit" leftIcon={RiSave2Line} disabled={!dirty} loading={save.isPending}>
                {t('save')}
              </Button>
            </div>
          )}
        </form>
      </Card>
      {unified && <SiteCard tenant={ctx.tenant} editable={rights.orgAdmin} />}
    </div>
  );
}
